// Creatividades: the library of every creative the person may see (GET /orgs/:orgId/creatives — RLS and the
// Client/Brand scope are applied by the server), with search, filters, order, thumbnails and publication status.
// An organization without creatives gets a welcome: start with VALE, with a prebuilt format, or look at demos.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { CreativeDocument } from "@interactads/creative-model";
import { Film, LayoutGrid, Plus, Radio, Search, SlidersHorizontal, Sparkles, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, call } from "@/api/client";
import { useFrontDesk, usePaged } from "@/api/hooks";
import valeImage from "@/assets/vale/vale-256.jpg";
import { formatDateTime, formatRelative } from "@/lib/format";
import { COMMON_FORMATS, PRODUCTS, VERSION_STATES } from "@/lib/labels";
import { Badge } from "@/ui/badge";
import { cn } from "@/ui/cn";
import { Button } from "@/ui/button";
import { Input, NativeSelect } from "@/ui/input";
import { EmptyState, PageHeader, Panel } from "@/ui/layout";
import { VersionStateBadge } from "@/components/status";
import { PageError, PageLoading, useTitle, useRouteParams } from "../common/page";
import { HierarchyFilters } from "../common/hierarchy-filters";
import { assetUrlFor } from "../studio/api";
import { Artboard } from "../studio/render/artboard";

const PAGE = 24;

interface Filters {
  q: string;
  clientId: string;
  brandId: string;
  campaignId: string;
  product: string;
  state: string;
  format: string;
  order: "newest" | "oldest";
}
const NO_FILTERS: Filters = {
  q: "",
  clientId: "",
  brandId: "",
  campaignId: "",
  product: "",
  state: "",
  format: "",
  order: "newest",
};
/** «Publicado» is not a version state: it means live in at least one Distribution. */
const LIVE = "LIVE";

function useLibrary(orgId: string, f: Filters) {
  const query = {
    limit: PAGE,
    order: f.order,
    ...(f.q ? { q: f.q } : {}),
    ...(f.clientId ? { clientId: f.clientId } : {}),
    ...(f.brandId ? { brandId: f.brandId } : {}),
    ...(f.campaignId ? { campaignId: f.campaignId } : {}),
    ...(f.product ? { product: f.product as never } : {}),
    ...(f.state === LIVE ? { published: true } : f.state ? { state: f.state as never } : {}),
    ...(f.format ? { format: f.format } : {}),
  };
  return usePaged(["org", orgId, "creative-library", query], (cursor) =>
    call(() =>
      api.GET("/orgs/{orgId}/creatives", {
        params: { path: { orgId }, query: { ...query, ...(cursor ? { cursor } : {}) } },
      })
    )
  );
}

export function CreativesPage() {
  const { orgId } = useRouteParams("orgId");
  const { data: fd, isLoading, error } = useFrontDesk(orgId);
  useTitle("Creatividades");
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [typed, setTyped] = useState("");
  // Phones: the filters fold under one button so the creatives stay on the first screen.
  const [showFilters, setShowFilters] = useState(false);
  // The search is sent a moment after the person stops typing.
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((f) => (f.q === typed.trim() ? f : { ...f, q: typed.trim() }));
    }, 300);
    return () => {
      clearTimeout(id);
    };
  }, [typed]);
  const library = useLibrary(orgId, filters);
  const filtered = JSON.stringify({ ...filters, order: "newest" }) !== JSON.stringify(NO_FILTERS);
  if (isLoading) return <PageLoading />;
  if (error || !fd) return <PageError error={error} orgId={orgId} />;
  const base = `/o/${orgId}`;
  const canCreate = fd.capabilities.createCreative;
  // No creative at all (and nothing filtered): the welcome, not an empty table.
  if (!filtered && !library.isLoading && library.items.length === 0 && fd.recentCreatives.length === 0)
    return <Welcome base={base} canCreate={canCreate} />;

  const set = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
  };
  return (
    <div className="grid gap-4">
      <PageHeader
        title="Creatividades"
        description="Todos los anuncios de tu espacio de trabajo que puedes ver."
        actions={
          canCreate ? (
            <Button asChild variant="primary">
              <Link to={`${base}/create`}>
                <Plus /> Crear creativo
              </Link>
            </Button>
          ) : null
        }
      />
      <Panel className="grid gap-2 p-2" data-testid="library-filters">
        {/* Desktop: search and every filter in two compact rows. Phone: the search, and the rest behind «Filtros». */}
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative sm:col-span-2">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted"
              aria-hidden="true"
            />
            <Input
              aria-label="Buscar por nombre"
              placeholder="Buscar por nombre"
              className="pl-8"
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value);
              }}
            />
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="h-full sm:hidden"
            aria-expanded={showFilters}
            aria-controls="library-filter-fields"
            onClick={() => {
              setShowFilters((v) => !v);
            }}
          >
            <SlidersHorizontal aria-hidden="true" /> Filtros{filtered ? " •" : ""}
          </Button>
          <div
            id="library-filter-fields"
            className={cn(
              "col-span-2 grid-cols-1 gap-2 sm:col-span-2 sm:grid sm:grid-cols-3 lg:col-span-3",
              showFilters ? "grid" : "hidden"
            )}
          >
            <HierarchyFilters orgId={orgId} value={filters} onChange={set} />
          </div>
          <div
            className={cn(
              "col-span-2 grid-cols-1 gap-2 sm:col-span-2 sm:grid sm:grid-cols-2 lg:col-span-5 lg:grid-cols-5",
              showFilters ? "grid" : "hidden"
            )}
          >
            <NativeSelect
              aria-label="Tipo"
              value={filters.product}
              onChange={(e) => {
                set({ product: e.target.value });
              }}
            >
              <option value="">Todos los tipos</option>
              {Object.entries(PRODUCTS).map(([id, p]) => (
                <option key={id} value={id}>
                  {p.label}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Estado"
              value={filters.state}
              onChange={(e) => {
                set({ state: e.target.value });
              }}
            >
              <option value="">Todos los estados</option>
              <option value={LIVE}>Publicado (entregando)</option>
              {Object.entries(VERSION_STATES).map(([id, s]) => (
                <option key={id} value={id}>
                  Última versión: {s.label}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Formato"
              value={filters.format}
              onChange={(e) => {
                set({ format: e.target.value });
              }}
            >
              <option value="">Todos los formatos</option>
              {COMMON_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect
              aria-label="Orden"
              value={filters.order}
              onChange={(e) => {
                set({ order: e.target.value === "oldest" ? "oldest" : "newest" });
              }}
            >
              <option value="newest">Más recientes primero</option>
              <option value="oldest">Más antiguos primero</option>
            </NativeSelect>
            {filtered ? (
              <Button
                size="sm"
                variant="ghost"
                className="h-full justify-self-start"
                onClick={() => {
                  setTyped("");
                  setFilters({ ...NO_FILTERS, order: filters.order });
                }}
              >
                <X aria-hidden="true" /> Quitar filtros
              </Button>
            ) : null}
          </div>
        </div>
      </Panel>

      {library.error ? (
        <PageError error={library.error} orgId={orgId} />
      ) : library.isLoading ? (
        <PageLoading />
      ) : library.items.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid />}
          title="Ninguna creatividad coincide"
          description="Prueba con otro nombre o quita algún filtro."
        />
      ) : (
        <>
          <ul className="grid gap-2" aria-label="Creatividades" data-testid="library">
            {library.items.map((c) => (
              <li key={c.id}>
                <CreativeCard orgId={orgId} base={base} c={c} />
              </li>
            ))}
          </ul>
          {library.hasNextPage ? (
            <Button
              variant="secondary"
              className="justify-self-center"
              disabled={library.isFetchingNextPage}
              onClick={() => void library.fetchNextPage()}
            >
              {library.isFetchingNextPage ? "Cargando…" : "Ver más"}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

type LibraryItem = ReturnType<typeof useLibrary>["items"][number];

function CreativeCard({ orgId, base, c }: { orgId: string; base: string; c: LibraryItem }) {
  const open = `${base}/creatives/${c.id}`;
  const formats = c.formats.map((f) => f.replace("x", " × ")).join(" · ");
  return (
    // Horizontal and compact: thumbnail · information · state and actions. On a phone the right column wraps
    // under the information; nothing has a fixed height, so nothing is cut.
    <article
      className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 rounded-md border border-line bg-surface p-3 transition-colors hover:border-line-strong md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center"
      aria-label={c.name}
      data-testid="library-card"
    >
      <Link to={open} className="row-span-2 self-start rounded-sm md:row-span-1" tabIndex={-1} aria-hidden="true">
        <CreativeThumb orgId={orgId} item={c} />
      </Link>
      <div className="grid min-w-0 content-start gap-1">
        <Link
          to={open}
          className="line-clamp-2 break-words text-sm font-semibold leading-snug hover:underline"
          title={c.name}
          data-testid="library-card-name"
        >
          {c.name}
        </Link>
        <p className="truncate text-xs text-muted" title={`${c.client.name} · ${c.brand.name} · ${c.campaign.name}`}>
          <span className="sr-only">Cliente, marca y campaña: </span>
          {c.client.name} · {c.brand.name} · {c.campaign.name}
        </p>
        <p className="text-xs text-muted">
          {PRODUCTS[c.product].label}
          {formats ? ` · ${formats}` : ""}
          {" · "}
          <span title={`Creado ${formatDateTime(c.createdAt)}`}>Actualizado {formatRelative(c.lastActivityAt)}</span>
        </p>
      </div>
      <div className="col-start-2 flex flex-wrap items-center gap-2 md:col-start-3 md:max-w-[22rem] md:justify-end">
        {c.latestVersion ? <VersionStateBadge state={c.latestVersion.state} /> : null}
        {c.liveDistributions > 0 ? (
          <Badge tone="success" title="Distribuciones que entregan una versión de este creativo">
            <Radio className="size-3" aria-hidden="true" /> En {c.liveDistributions}{" "}
            {c.liveDistributions === 1 ? "distribución" : "distribuciones"}
          </Badge>
        ) : (
          <span className="text-xs text-muted">Sin publicar</span>
        )}
        <span className="flex gap-1.5">
          <Button asChild size="sm" variant="secondary">
            <Link to={open}>Abrir</Link>
          </Button>
          {c.latestVersion ? (
            <Button asChild size="sm" variant="ghost">
              <Link to={`${base}/versions/${c.latestVersion.id}/studio`}>Abrir en el Studio</Link>
            </Button>
          ) : null}
        </span>
      </div>
    </article>
  );
}

/** Thumbnail box: every format fits inside at its own proportions (never stretched, never cropped). */
const THUMB = { w: 112, h: 96 } as const;

/**
 * The latest version, drawn once the card is on screen (one request per visible card): the first scene at rest
 * of a canonical document; for an HTML5 ad, its uploaded fallback image if it has one (an HTML5 project only runs
 * in the isolated preview origin). Otherwise a discreet placeholder.
 */
function CreativeThumb({ orgId, item }: { orgId: string; item: LibraryItem }) {
  const box = useRef<HTMLDivElement>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el || seen) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [seen]);
  const versionId = item.latestVersion?.id;
  const version = useQuery({
    queryKey: ["org", orgId, "version", versionId],
    queryFn: () =>
      call(() =>
        api.GET("/orgs/{orgId}/versions/{versionId}", { params: { path: { orgId, versionId: versionId ?? "" } } })
      ),
    enabled: seen && Boolean(versionId),
  });
  const doc = useMemo(() => {
    const content = version.data?.content as Partial<CreativeDocument> | undefined;
    return content?.schemaVersion === 2 && content.layouts?.[0]?.sizing.mode === "fixed"
      ? (content as CreativeDocument)
      : null;
  }, [version.data]);
  const fit = (w: number, h: number) => Math.min(1, THUMB.h / h, THUMB.w / w);
  const placeholder = (label: string, icon: React.ReactNode) => (
    <span className="grid h-full place-items-center gap-0.5 text-center text-2xs text-muted">
      {icon}
      {label}
    </span>
  );
  let body: React.ReactNode;
  const layout = doc?.layouts[0];
  const sizing = layout?.sizing.mode === "fixed" ? layout.sizing : null;
  if (doc && layout && sizing && item.html5) {
    const fb = doc.fallback?.perLayout[layout.id];
    const asset = fb && "asset" in fb ? doc.assets.find((a) => a.key === fb.asset) : undefined;
    const scale = fit(sizing.width, sizing.height);
    body = asset ? (
      <img
        src={assetUrlFor(orgId)(asset)}
        alt=""
        className="rounded-sm object-contain shadow-sm"
        style={{ width: sizing.width * scale, height: sizing.height * scale }}
      />
    ) : (
      placeholder("HTML5", <Sparkles className="size-4" aria-hidden="true" />)
    );
  } else if (doc && layout && sizing) {
    const scale = fit(sizing.width, sizing.height);
    body = (
      <div
        className="overflow-hidden rounded-sm shadow-sm"
        style={{ width: sizing.width * scale, height: sizing.height * scale }}
      >
        <div
          style={{
            width: sizing.width,
            height: sizing.height,
            transform: `scale(${String(scale)})`,
            transformOrigin: "0 0",
          }}
        >
          <Artboard doc={doc} layoutId={layout.id} mode="preview" assetUrl={assetUrlFor(orgId)} />
        </div>
      </div>
    );
  } else if (item.html5 && seen && !version.isLoading)
    body = placeholder("HTML5", <Sparkles className="size-4" aria-hidden="true" />);
  else if (seen && !version.isLoading)
    body = placeholder(
      "Sin diseño",
      item.product === "VIDEO_RICH_MEDIA" ? (
        <Film className="size-4" aria-hidden="true" />
      ) : (
        <LayoutGrid className="size-4" aria-hidden="true" />
      )
    );
  return (
    <div
      ref={box}
      className="pointer-events-none grid place-items-center overflow-hidden rounded-sm bg-sunken"
      style={{ width: THUMB.w + 8, height: THUMB.h + 8 }}
      data-testid="library-thumb"
      inert
    >
      {body ?? <div className="size-full animate-pulse bg-line/40" />}
    </div>
  );
}

/** First visit with no creative: what InteractAds does and the three ways to begin. */
function Welcome({ base, canCreate }: { base: string; canCreate: boolean }) {
  return (
    <div className="grid gap-6" data-testid="creatives-welcome">
      <PageHeader title="Creatividades" />
      <Panel className="grid items-center gap-6 p-6 md:grid-cols-[auto_minmax(0,1fr)]">
        <img
          src={valeImage}
          alt="VALE, la asistente creativa de InteractAds"
          className="mx-auto h-48 w-auto rounded-lg object-contain md:h-56"
        />
        <div className="grid gap-4">
          <div className="grid gap-1">
            <h2 className="text-xl font-semibold">Crea tu primer anuncio interactivo</h2>
            <p className="text-sm text-muted">
              Aquí verás todos tus anuncios. Empieza conversando con VALE o eligiendo un formato prediseñado: juegos,
              encuestas, video y banners que personalizas sin programar.
            </p>
          </div>
          <ol className="grid gap-2 text-sm sm:grid-cols-3">
            {[
              ["1. Crea", "Con VALE o con un formato listo para personalizar."],
              ["2. Publica", "Revisa, aprueba y elige la plataforma."],
              ["3. Usa tu Tag", "Cópialo en tu plataforma y mide los resultados aquí."],
            ].map(([title, text]) => (
              <li key={title} className="grid gap-0.5 rounded-md border border-line bg-sunken p-3">
                <span className="font-medium">{title}</span>
                <span className="text-muted">{text}</span>
              </li>
            ))}
          </ol>
          {canCreate ? (
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="primary">
                <Link to={`${base}/create`} search={{ start: "vale" }}>
                  <Sparkles aria-hidden="true" /> Crear con VALE
                </Link>
              </Button>
              <Button asChild variant="secondary">
                <Link to={`${base}/create`} search={{ start: "prebuilt" }}>
                  <LayoutGrid aria-hidden="true" /> Ver formatos prediseñados y sus demostraciones
                </Link>
              </Button>
            </div>
          ) : (
            <p className="text-sm text-muted">Tu rol no permite crear creativos. Pide acceso a un administrador.</p>
          )}
        </div>
      </Panel>
    </div>
  );
}
